package org.dronda.app.web.ui.eventadmin

import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import app.cash.molecule.RecompositionMode
import app.cash.molecule.launchMolecule
import com.github.michaelbull.result.andThen
import com.github.michaelbull.result.getOrElse
import com.github.michaelbull.result.map
import com.github.michaelbull.result.mapError
import com.github.michaelbull.result.onErr
import com.github.michaelbull.result.onOk
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import org.dronda.api.wishlist.UpdateEventRequest
import org.dronda.api.wishlist.toApi
import org.dronda.app.web.repository.EventRepository
import org.dronda.app.web.ui.components.SelectOption
import org.dronda.app.web.ui.util.collectEventSink
import org.dronda.app.web.ui.util.exclusionDirectionChoices
import org.dronda.service.wishlist.core.domain.EventAdminDetails
import org.dronda.service.wishlist.core.domain.EventMode
import org.dronda.service.wishlist.core.domain.Exclusion
import org.dronda.service.wishlist.core.domain.SantaExclusionDirection
import org.dronda.service.wishlist.core.domain.SantaSendKind
import kotlin.uuid.Uuid

class EventAdminViewModel(
    private val eventId: String,
    private val eventRepository: EventRepository,
) {
    private val scope = CoroutineScope(Dispatchers.Main)
    private val eventSink = MutableSharedFlow<Event>(extraBufferCapacity = 8)

    val uiState: StateFlow<UiState> = scope.launchMolecule(mode = RecompositionMode.Immediate) {
        var admin by remember { mutableStateOf<EventAdminDetails?>(null) }
        var loadError by remember { mutableStateOf<String?>(null) }

        var name by remember { mutableStateOf("") }
        var date by remember { mutableStateOf("") }
        var budget by remember { mutableStateOf("") }
        var mode by remember { mutableStateOf(EventMode.STANDARD) }
        var settingsMessage by remember { mutableStateOf<String?>(null) }

        var emailDraft by remember { mutableStateOf("") }
        var addPersonError by remember { mutableStateOf<String?>(null) }

        var exclusions by remember { mutableStateOf(listOf<Exclusion>()) }
        var exclusionA by remember { mutableStateOf<String?>(null) }
        var exclusionB by remember { mutableStateOf<String?>(null) }
        var exclusionDirection by remember { mutableStateOf(SantaExclusionDirection.BOTH) }
        var exclusionsMessage by remember { mutableStateOf<String?>(null) }

        var selectedKind by remember { mutableStateOf<SantaSendKind?>(null) }
        var confirmingSend by remember { mutableStateOf(false) }
        var sendMessage by remember { mutableStateOf<String?>(null) }
        var busy by remember { mutableStateOf(false) }

        fun apply(details: EventAdminDetails) {
            admin = details
            name = details.event.name
            date = details.event.date.toString()
            budget = details.event.budget.orEmpty()
            mode = details.event.mode
            exclusions = details.santa?.exclusions.orEmpty()
            val allowed = details.santa?.allowedSendKinds.orEmpty()
            selectedKind = selectedKind?.takeIf { it in allowed } ?: allowed.firstOrNull()
        }

        suspend fun reload() {
            eventRepository.getAdminDetails(eventId)
                .onOk { apply(it) }
                .onErr { loadError = it }
        }

        suspend fun runBusy(block: suspend () -> Unit) {
            busy = true
            try {
                block()
            } finally {
                busy = false
            }
        }

        LaunchedEffect(Unit) { reload() }

        collectEventSink(eventSink) { event ->
            when (event) {
                is Event.NameChanged -> name = event.value
                is Event.DateChanged -> date = event.value
                is Event.BudgetChanged -> budget = event.value
                is Event.ModeChanged -> mode = event.mode
                Event.SaveSettings -> scope.launch {
                    runBusy {
                        settingsMessage = saveSettings(name, date, mode, budget)
                        reload()
                    }
                }
                is Event.EmailDraftChanged -> {
                    emailDraft = event.value
                    addPersonError = null
                }
                Event.AddPerson -> scope.launch {
                    val email = emailDraft.trim()
                    if (email.isEmpty()) return@launch
                    runBusy {
                        eventRepository.addParticipant(eventId, email)
                            .onOk {
                                emailDraft = ""
                                reload()
                            }
                            .onErr { addPersonError = it }
                    }
                }
                is Event.ExclusionAChanged -> exclusionA = event.userId
                is Event.ExclusionBChanged -> exclusionB = event.userId
                is Event.ExclusionDirectionChanged -> exclusionDirection = event.direction
                Event.AddExclusion -> {
                    val a = exclusionA?.let(Uuid::parse)
                    val b = exclusionB?.let(Uuid::parse)
                    if (a != null && b != null && a != b && !exclusions.containsPair(a, b)) {
                        exclusions = exclusions + Exclusion(a, b, exclusionDirection)
                        exclusionA = null
                        exclusionB = null
                        exclusionDirection = SantaExclusionDirection.BOTH
                        exclusionsMessage = null
                    }
                }
                is Event.RemoveExclusion -> {
                    exclusions = exclusions - event.exclusion
                    exclusionsMessage = null
                }
                Event.SaveExclusions -> scope.launch {
                    runBusy {
                        eventRepository.updateExclusions(eventId, exclusions)
                            .onOk {
                                apply(it)
                                exclusionsMessage = "Exclusions saved"
                            }
                            .onErr { exclusionsMessage = it }
                    }
                }
                is Event.SendKindSelected -> selectedKind = event.kind
                Event.RequestSend -> confirmingSend = true
                Event.CancelSend -> confirmingSend = false
                Event.ConfirmSend -> scope.launch {
                    confirmingSend = false
                    val kind = selectedKind ?: return@launch
                    runBusy {
                        eventRepository.sendAssignments(eventId, kind)
                            .onOk { response ->
                                sendMessage = buildString {
                                    append("Sent ${response.emailsSent} ${if (response.emailsSent == 1) "email" else "emails"}.")
                                    if (response.emailsFailed > 0) append(" ${response.emailsFailed} failed to send.")
                                }
                            }
                            .onErr { sendMessage = it }
                        reload()
                    }
                }
            }
        }

        val loaded = admin
        UiState(
            loading = loaded == null && loadError == null,
            loadError = loadError,
            title = loaded?.event?.name.orEmpty(),
            name = name,
            date = date,
            budget = budget,
            mode = mode,
            canChangeMode = loaded?.santa?.isDrawn != true,
            settingsMessage = settingsMessage,
            peopleTitle = loaded?.let { "People (${it.participants.size} / ${it.maxParticipants})" }.orEmpty(),
            people = remember(loaded) { loaded?.let { personRows(it, TimeZone.currentSystemDefault()) }.orEmpty() },
            canAddPeople = loaded != null && loaded.participants.size < loaded.maxParticipants,
            emailDraft = emailDraft,
            addPersonError = addPersonError,
            isSecretSanta = loaded?.santa != null,
            choices = remember(loaded) { loaded?.let(::choices).orEmpty() },
            exclusionA = exclusionA,
            exclusionB = exclusionB,
            exclusionDirection = exclusionDirection,
            directionChoices = exclusionDirectionChoices,
            exclusions = remember(loaded, exclusions) { loaded?.let { exclusionRows(it, exclusions) }.orEmpty() },
            exclusionsDirty = exclusions.toSet() != loaded?.santa?.exclusions.orEmpty().toSet(),
            exclusionsMessage = exclusionsMessage,
            sendPanel = remember(loaded) { loaded?.let(::sendPanel) },
            selectedKind = selectedKind,
            confirmingSend = confirmingSend,
            sendMessage = sendMessage,
            busy = busy,
        )
    }

    fun take(event: Event) {
        eventSink.tryEmit(event)
    }

    private suspend fun saveSettings(name: String, date: String, mode: EventMode, budget: String): String {
        return UpdateEventRequest.from(
            name = name.trim(),
            date = date,
            mode = mode.toApi(),
            budget = budget.trim().takeIf { mode == EventMode.SECRET_SANTA && it.isNotEmpty() },
        )
            .mapError { "Check the name, date and budget and try again" }
            .andThen { request -> eventRepository.updateEvent(eventId, request) }
            .map { "Settings saved" }
            .getOrElse { it }
    }

    data class UiState(
        val loading: Boolean,
        val loadError: String?,
        val title: String,
        val name: String,
        val date: String,
        val budget: String,
        val mode: EventMode,
        val canChangeMode: Boolean,
        val settingsMessage: String?,
        val peopleTitle: String,
        val people: List<PersonRow>,
        val canAddPeople: Boolean,
        val emailDraft: String,
        val addPersonError: String?,
        val isSecretSanta: Boolean,
        val choices: List<SelectOption>,
        val exclusionA: String?,
        val exclusionB: String?,
        val exclusionDirection: SantaExclusionDirection,
        val directionChoices: List<SelectOption>,
        val exclusions: List<ExclusionRow>,
        val exclusionsDirty: Boolean,
        val exclusionsMessage: String?,
        val sendPanel: SendPanel?,
        val selectedKind: SantaSendKind?,
        val confirmingSend: Boolean,
        val sendMessage: String?,
        val busy: Boolean,
    )

    sealed interface Event {
        data class NameChanged(val value: String) : Event
        data class DateChanged(val value: String) : Event
        data class BudgetChanged(val value: String) : Event
        data class ModeChanged(val mode: EventMode) : Event
        data object SaveSettings : Event
        data class EmailDraftChanged(val value: String) : Event
        data object AddPerson : Event
        data class ExclusionAChanged(val userId: String?) : Event
        data class ExclusionBChanged(val userId: String?) : Event
        data class ExclusionDirectionChanged(val direction: SantaExclusionDirection) : Event
        data object AddExclusion : Event
        data class RemoveExclusion(val exclusion: Exclusion) : Event
        data object SaveExclusions : Event
        data class SendKindSelected(val kind: SantaSendKind) : Event
        data object RequestSend : Event
        data object CancelSend : Event
        data object ConfirmSend : Event
    }
}
